import { removeTrapFocus } from '@theme/focus';

class TrekcampCatalogTools extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const options = {signal:this.abort.signal};
    this.copy = JSON.parse(this.querySelector('[data-tc-copy]').textContent);
    this.form = this.querySelector('[data-tc-filter]');
    this.storageKey = 'trekcamp-compare:' + (this.form?.dataset.collectionUrl || this.dataset.collectionUrl || location.pathname);
    // Each Compare session begins unselected. Discard selections saved by
    // earlier versions of the collection comparison feature.
    this.selected = new Map();
    try { sessionStorage.removeItem(this.storageKey); } catch {}
    this.dialog = this.querySelector('dialog');
    this.modeOpen = false;
    this.root = this.closest('[data-template]');
    this.toggle = this.querySelector('[data-tc-mode-toggle]');
    this.panel = this.querySelector('[data-tc-compare-controls]');
    this.openButton = this.panel?.querySelector('[data-tc-open]');
    this.clearButton = this.panel?.querySelector('[data-tc-clear]');
    this.status = this.panel?.querySelector('[data-tc-status]');
    this.stickyBar = this.querySelector('[data-tc-sticky-compare]');
    this.stickyOpen = this.stickyBar?.querySelector('[data-tc-sticky-open]');
    this.stickyClear = this.stickyBar?.querySelector('[data-tc-sticky-clear]');
    this.stickyClose = this.stickyBar?.querySelector('[data-tc-sticky-close]');
    this.stickyCount = this.stickyBar?.querySelector('[data-tc-sticky-count]');
    this.stickyNote = this.stickyBar?.querySelector('[data-tc-sticky-note]');
    this.limitNotice = false;
    if (this.stickyBar && document.body) document.body.append(this.stickyBar);
    // Reserve only the height actually occupied by the floating Compare bar,
    // including extra lines when its three-item warning wraps.
    this.updateStickySpace = () => {
      if (!this.root || !this.stickyBar || this.stickyBar.hidden) return;
      const bar = this.stickyBar.getBoundingClientRect();
      if (!bar.height) return;
      const bottomGap = Math.max(0, window.innerHeight - bar.bottom);
      const clearance = Math.ceil(bar.height + bottomGap + 20) + 'px';
      if (this.root.style.getPropertyValue('--tc-compare-sticky-clearance') !== clearance) {
        this.root.style.setProperty('--tc-compare-sticky-clearance', clearance);
      }
    };
    if (this.stickyBar && typeof ResizeObserver !== 'undefined') {
      this.stickySizeObserver = new ResizeObserver(this.updateStickySpace);
      this.stickySizeObserver.observe(this.stickyBar);
    }
    this.updateSticky = () => {
      if (!this.stickyBar) return;
      // Compare workspace is visible immediately, never tied to scroll.
      const blocked = !!document.querySelector('theme-drawer[open]') || !!this.dialog?.open;
      const show = this.modeOpen && !blocked;
      if (this.stickyBar.hidden !== !show) this.stickyBar.hidden = !show;
      if (show) this.updateStickySpace();
    };
    // Follow the filter state, not sort order or current pagination.
    this.filterSignature = () => {
      const url = new URL(location.href);
      const facets = [...url.searchParams.entries()]
        .filter(([key]) => key.startsWith('filter.'))
        .sort(([a, av], [b, bv]) => a.localeCompare(b) || av.localeCompare(bv));
      return url.pathname + '?' + new URLSearchParams(facets).toString();
    };
    this.lastRenderedFilterSignature = this.filterSignature();
    this.pruneCompareSelections = () => {
      if (!this.modeOpen || !this.root || this.abort?.signal.aborted) return false;
      const grid = this.root.querySelector('.main-collection-grid .product-grid');
      const empty = this.root.querySelector('.main-collection-grid__empty, .tc-empty-collection');
      // A temporarily absent grid is not an empty result. A real zero-result
      // response has the server-rendered empty message inside the grid.
      if (!grid && !empty) return false;
      const availableIds = new Set(
        [...(grid?.querySelectorAll('.product-grid__item[data-product-id]') || [])]
          .map(item => String(item.dataset.productId))
      );
      if (!availableIds.size && !empty) return false;

      let removed = false;
      for (const id of this.selected.keys()) {
        if (!availableIds.has(id)) {
          this.selected.delete(id);
          removed = true;
        }
      }
      if (removed) {
        this.limitNotice = false;
        if (this.status) this.status.textContent = '';
        // sync refreshes the sticky 0/3-3/3 counter, item outlines,
        // compare-button enabled state AND dialog's selected product data.
        this.sync();
      }
      return true;
    };
    this.mountComparePanel = () => {
      const slot = this.root?.querySelector('[data-tc-compare-slot]');
      if (slot && this.panel && this.panel.parentElement !== slot) slot.append(this.panel);
    };
    // Apply TREKCAMP tag selections using the SAME Shopify section renderer as
    // Search & Discovery. Product results and native facets refresh without a
    // full page navigation; browser history, sort, and native query filters stay.
    this.applyCustomFilters = form => {
      if (!(form instanceof HTMLFormElement)) return;
      const selectors = [...form.querySelectorAll('select[data-tc-tag-filter]')];
      const tagsFromPage = form.querySelector('[data-tc-current-tags]')?.textContent || '[]';
      let existingTags = [];
      try { existingTags = JSON.parse(tagsFromPage); } catch {}
      const managedPrefixes = selectors.map(select => select.dataset.tagPrefix + '-');
      const visibleTags = new Set(selectors.flatMap(select =>
        [...select.options].map(option => option.value).filter(Boolean)
      ));
      const remaining = existingTags.filter(tag =>
        !visibleTags.has(tag) && !managedPrefixes.some(prefix => tag.startsWith(prefix))
      );
      const requested = selectors.map(select => select.value).filter(Boolean);
      const tags = [...new Set([...remaining, ...requested])];
      const url = new URL(form.dataset.collectionUrl, location.origin);
      if (tags.length) url.pathname += '/' + tags.map(tag => encodeURIComponent(tag)).join('+');
      url.search = location.search;
      url.searchParams.delete('page');
      if (url.href === location.href) return;

      const facetsForm = this.root?.querySelector('#filters-drawer facets-form-component')
        || this.root?.querySelector('facets-form-component');
      if (typeof facetsForm?.updateFiltersByURL === 'function') {
        facetsForm.updateFiltersByURL(url.href);
      } else {
        // Only used when the Shopify native facet controller is unavailable.
        location.assign(url.href);
      }
    };
    document.addEventListener('click', event => {
      const anchor = event.target.closest?.('[data-tc-filter-chip], [data-tc-filter-clear]');
      if (!(anchor instanceof HTMLAnchorElement) || !this.root?.contains(anchor)) return;
      const facetsForm = this.root.querySelector('#filters-drawer facets-form-component')
        || this.root.querySelector('facets-form-component');
      if (typeof facetsForm?.updateFiltersByURL !== 'function') return;
      event.preventDefault();
      // Clear All always targets the canonical UNFILTERED collection URL.
      // A single native chip retains all other native facets; a tc-* chip
      // retains the current native query/sorting while removing just one tag.
      const isClear = anchor.hasAttribute('data-tc-filter-clear');
      const canonical = new URL(this.dataset.collectionUrl || location.pathname, location.origin);
      const url = isClear ? canonical : new URL(anchor.href, location.href);
      if (anchor.hasAttribute('data-tc-tag-chip')) url.search = location.search;
      if (isClear) url.search = '';
      url.searchParams.delete('page');
      url.hash = '';
      if (url.origin !== location.origin || url.href === location.href) return;

      // Keep the Testing Theme preview identity on Shopify's AJAX request,
      // even if the native filter's remove URL omitted preview-only parameters.
      const current = new URL(location.href);
      for (const key of ['preview_theme_id', 'preview_key']) {
        const value = current.searchParams.get(key);
        if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
      }
      // ALL chip removals, including the final one, use the same native
      // Shopify section renderer. No page reload or special-case navigation.
      facetsForm.updateFiltersByURL(url.href);
    }, options);
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-tc-filter]')) return;
      event.preventDefault();
      this.applyCustomFilters(form);
    }, options);
    // Update the combined badge immediately while the native Shopify section
    // fetch is pending. The rendered URL then becomes the source of truth.
    this.refreshLiveFilterCount = () => {
      const form = this.root?.querySelector('form[data-tc-filter]');
      const source = this.root?.querySelector('.tc-collection-toolbar-filter[data-tc-native-count]')
        || this.root?.querySelector('.facets-toggle__button[data-tc-native-count]');
      if (!form || !source) return;
      const selects = [...form.querySelectorAll('select[data-tc-tag-filter]')];
      if (!selects.length) return;

      let appliedTags = [];
      try {
        appliedTags = JSON.parse(form.querySelector('[data-tc-current-tags]')?.textContent || '[]');
      } catch { /* fall back to the server's applied-count baseline */ }
      const managedPrefixes = selects.map(select => select.dataset.tagPrefix + '-');
      const appliedManagedCount = appliedTags.filter(tag =>
        managedPrefixes.some(prefix => tag.startsWith(prefix))
      ).length;
      const selectedManagedCount = selects.filter(select => select.value !== '').length;
      const nativeCount = Math.max(0, Number(source.dataset.tcNativeCount) || 0);
      const appliedCustomCount = Math.max(0, Number(source.dataset.tcCustomCount) || 0);
      const customCount = Math.max(0, appliedCustomCount - appliedManagedCount + selectedManagedCount);
      const total = nativeCount + customCount;
      const number = String(total);
      const show = total > 0;

      const desktop = this.root.querySelector('.tc-toolbar-filter-count');
      const mobile = this.root.querySelector('#filter-count-bubble-toggle');
      const drawer = this.root.querySelector('#filters-drawer-bubble');
      for (const badge of [desktop, mobile, drawer]) {
        if (!badge) continue;
        if (badge.hidden === show) badge.hidden = !show;
        const textNode = badge === mobile ? badge.querySelector('.filter-count-bubble__text') : badge;
        if (textNode && textNode.textContent.trim() !== number) textNode.textContent = number;
      }
      const ariaLabel = total === 1 ? '1 filter selected' : number + ' filters selected';
      if (mobile && mobile.getAttribute('aria-label') !== ariaLabel) mobile.setAttribute('aria-label', ariaLabel);
      const sr = drawer?.parentElement?.querySelector('[data-tc-filter-count-sr]');
      if (sr) {
        if (sr.hidden === show) sr.hidden = !show;
        if (sr.textContent.trim() !== ariaLabel) sr.textContent = ariaLabel;
      }
    };
    document.addEventListener('change', event => {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.matches('select[data-tc-tag-filter]')) return;
      if (!this.root?.contains(select)) return;
      this.refreshLiveFilterCount();
      this.applyCustomFilters(select.form);
    }, options);
    this.sync = () => {
      this.mountComparePanel();
      this.refreshLiveFilterCount();
      const drawer = document.getElementById('filters-drawer');
      const filterToggle = this.querySelector('[data-tc-unified-toggle]');
      if (drawer && typeof drawer.open === 'function' && filterToggle) {
        filterToggle.hidden = false;
        if (!this.root?.hasAttribute('data-tc-unified-filters')) this.root?.setAttribute('data-tc-unified-filters', '');
        const expanded = drawer.hasAttribute('open');
        filterToggle.setAttribute('aria-expanded', String(expanded));
        const icon = filterToggle.querySelector('[data-tc-filter-icon]');
        const label = expanded ? '−' : '+';
        if (icon && icon.textContent !== label) icon.textContent = label;
      }

      if (!drawer && filterToggle) { filterToggle.hidden = true; this.root?.removeAttribute('data-tc-unified-filters'); }
      this.root?.querySelectorAll('[data-tc-toolbar-compare]').forEach(button => {
        button.setAttribute('aria-pressed', String(this.modeOpen));
      });
      this.root?.querySelectorAll('.product-grid__item [data-tc-compare]').forEach(button => {
        button.hidden = !this.modeOpen;
        const card = button.closest('product-card');
        const source = card?.querySelector('[data-tc-product]');
        if (!card || !source) return;
        const product = JSON.parse(source.textContent);
        const key = String(product.id);
        const chosen = this.selected.has(key);
        if (chosen) this.selected.set(key,product);
        button.setAttribute('aria-checked',String(chosen));
        const label = chosen ? this.copy.remove : this.copy.compare;
        button.setAttribute('aria-label',label + ' ' + product.title);
        button.title = label;
        card.toggleAttribute('data-tc-compare-selected',chosen && this.modeOpen);
      });
      const count = this.selected.size;
      if (this.openButton) this.openButton.disabled = count < 2;
      if (this.stickyOpen) this.stickyOpen.disabled = count < 2;
      const counter = '(' + count + '/3)';
      if (this.stickyCount && this.stickyCount.textContent !== counter) this.stickyCount.textContent = counter;
      if (this.stickyNote) {
        const note = count >= 3
          ? (this.limitNotice ? 'Maximum 3 products selected — remove one to choose another.' : 'Maximum 3 products selected.')
          : 'Select up to 3 products to compare.';
        if (this.stickyNote.textContent !== note) this.stickyNote.textContent = note;
      }
      this.updateSticky();
    };
    this.setMode = (open) => {
      // Closing Compare Mode starts a clean comparison next time.
      // Closing the comparison dialog alone does NOT clear selected products.
      if (!open) {
        this.selected.clear();
        this.limitNotice = false;
        if (this.status) this.status.textContent = '';
        this.querySelector('[data-tc-table]')?.replaceChildren();
      }
      this.modeOpen = open;
      this.root?.setAttribute('data-tc-compare-mode', open ? 'open' : 'closed');
      this.toggle?.setAttribute('aria-expanded', String(open));
      if (this.panel) this.panel.hidden = !open;
      const icon = this.toggle?.querySelector('[data-tc-mode-icon]');
      if (icon) icon.textContent = open ? '−' : '+';
      this.sync();
    };
    this.toggle?.addEventListener('click', () => this.setMode(!this.modeOpen), options);
    document.addEventListener('click', event => {
      const compare = event.target.closest('[data-tc-toolbar-compare]');
      if (!compare || !this.root?.contains(compare)) return;
      event.preventDefault();
      this.setMode(!this.modeOpen);
    }, options);
    document.addEventListener('click' ,event => {
      const button = event.target.closest('.product-grid__item [data-tc-compare]');
      if (!button || !this.modeOpen || !this.root?.contains(button)) return;
      event.preventDefault();event.stopPropagation();
      const card = button.closest('product-card');
      const product = JSON.parse(card.querySelector('[data-tc-product]').textContent);
      const key = String(product.id);
      const status = this.status;
      if (this.selected.has(key)) this.selected.delete(key);
      else if (this.selected.size < 3) this.selected.set(key,product);
      else {
        this.limitNotice = true;
        if (status) status.textContent=this.copy.limit;
        this.sync();
        return;
      }
      this.limitNotice = false;
      if (status) status.textContent='';
      this.sync();
    },{...options,capture:true});
    this.clearButton?.addEventListener('click',()=>{this.selected.clear();this.limitNotice=false;if(this.status)this.status.textContent='';this.sync();},options);
    this.stickyClose?.addEventListener('click',()=>this.setMode(false),options);
    this.stickyOpen?.addEventListener('click',()=>{
      if (!this.modeOpen || this.selected.size < 2) return;
      this.openButton?.click();
      this.updateSticky();
    },options);
    this.stickyClear?.addEventListener('click',()=>{
      if (!this.modeOpen) return;
      this.clearButton?.click();
    },options);
    this.openButton?.addEventListener('click',()=>{
      const table=document.createElement('table');
      const products=[...this.selected.values()];
      const heading=table.createTHead().insertRow();
      const feature=document.createElement('th');feature.scope='col';feature.textContent=this.copy.feature;heading.append(feature);
      products.forEach(product=>{const cell=document.createElement('th');cell.scope='col';const link=document.createElement('a');link.href=product.url;link.textContent=product.title;cell.append(link);heading.append(cell);});
      const body=table.createTBody();
      const hasValue = value => value !== null && value !== undefined && value !== '';
      const allKind = kind => products.every(product => product.type === kind);
      const commonRows = [
        ['price',this.copy.price],
        ['capacity',allKind('hiking') ? 'Volume / Capacity' : allKind('cooking') ? 'Vessel Capacity' : this.copy.capacity],
        ['setup',this.copy.setup],
        ['dimensions',allKind('furniture') ? 'Open Dimensions' : allKind('hiking') ? 'Dimensions / Pole Length' : this.copy.dimensions],
        ['fabric',allKind('tent') ? this.copy.fabric : 'Material'],
        ['weight',products.every(product => hasValue(product.packed_weight)) ? 'Product Weight (Without Packaging)' : this.copy.weight],
        ['packed_weight',this.copy.packed_weight],
        ['packed_size',this.copy.packed_size],
        ['included',this.copy.included],
        ['warranty',this.copy.warranty]
      ];
      const addSpecification = (label, getValue) => {
        const values = products.map(getValue);
        // Do not clutter comparisons with entire rows of missing information.
        if (!values.some(hasValue)) return;
        const row = body.insertRow();
        const heading = document.createElement('th');
        heading.scope = 'row';
        heading.textContent = label;
        row.append(heading);
        values.forEach(value => {
          row.insertCell().textContent = hasValue(value) ? String(value) : this.copy.missing;
        });
      };
      commonRows.forEach(([key,label]) => addSpecification(label, product => product[key]));
      const smartFields = [
        ['tent_inner_dimensions','Interior Dimensions'],
        ['tent_frame_material','Frame Material'],
        ['tent_waterproof_rating_mm','Waterproof Rating','mm'],
        ['tent_door_count','Doors'],
        ['tent_window_count','Windows'],
        ['tent_ventilation','Ventilation'],
        ['tent_floor','Floor / Groundsheet'],
        ['furniture_folded_dimensions','Folded Dimensions'],
        ['furniture_max_load_kg','Maximum Load','kg'],
        ['backpack_compartments','Compartments'],
        ['backpack_carry_support','Carry & Back Support'],
        ['backpack_rain_cover','Rain Cover Included'],
        ['sleeping_comfort_temp_c','Comfort Temperature','°C'],
        ['sleeping_limit_temp_c','Limit Temperature','°C'],
        ['sleeping_insulation','Insulation'],
        ['sleeping_shape','Shape / Design'],
        ['cooking_fuel_type','Fuel Type'],
        ['cooking_burner_count','Burners'],
        ['cooking_output_w','Rated Output','W'],
        ['cooking_compatibility','Compatibility']
      ];
      smartFields.forEach(([key,label,unit])=>{
        if (key === 'sleeping_insulation' && products.every(product =>
          !hasValue(product.smart?.[key]) || product.smart[key] === product.fabric)) return;
        addSpecification(label, product => {
          const value = product.smart?.[key];
          if (!hasValue(value)) return null;
          if (typeof value === 'boolean') return value ? 'Yes' : 'No';
          return unit ? String(value) + ' ' + unit : String(value);
        });
      });
      const region=this.querySelector('[data-tc-table]');region.replaceChildren(table);region.scrollLeft=0;this.dialog.showModal();
    },options);
    this.querySelector('[data-tc-close]')?.addEventListener('click',()=>this.dialog.close(),options);
    this.dialog?.addEventListener('close',this.updateSticky,options);
    window.addEventListener('scroll',this.updateSticky,{...options,passive:true});
    window.addEventListener('resize',this.updateSticky,options);
    const results=this.root;
    if (results) {
      // Restore checkbox/outline state when Shopify changes the product DOM,
      // but do NOT prune while a section is still rendering.
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(results, {childList:true,subtree:true});
    }
    this.querySelector('[data-tc-unified-toggle]')?.addEventListener('click', () => document.getElementById('filters-drawer')?.toggle(), options);

    // The desktop filter sidebar's focus trap can hijack the first header
    // click, and immediately closing the drawer shrinks page-wrapper while
    // the click is still being dispatched. Do neither on hover.
    // Release focus on outside pointerdown WITHOUT moving the layout; defer
    // the close until AFTER the clicked link has performed its native action.
    const outsideFilters = (event) => {
      if (!(event.target instanceof Element)) return null;
      const drawer = document.getElementById('filters-drawer');
      if (!drawer?.hasAttribute('open') || typeof drawer.close !== 'function') return null;
      if (drawer.contains(event.target)) return null;
      // The filter triggers already use #filters-drawer/toggle.
      if (event.target.closest('.tc-collection-toolbar-filter, .facets-toggle__button, [data-tc-unified-toggle]')) return null;
      return drawer;
    };
    const releaseFocusBeforeOutsideClick = (event) => {
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      if (!outsideFilters(event)) return;
      // A different open drawer may own the global focus trap.
      if (document.querySelector('theme-drawer[open]:not(#filters-drawer)')) return;
      removeTrapFocus();
    };
    const closeFiltersAfterOutsideClick = (event) => {
      const drawer = outsideFilters(event);
      if (!drawer) return;
      // The browser performs the native link default action before this
      // task runs. This avoids both focus theft and header layout shifts.
      window.setTimeout(() => {
        if (!this.abort?.signal.aborted && drawer.isConnected && drawer.hasAttribute('open')) {
          void drawer.close();
        }
      }, 0);
    };
    document.addEventListener('pointerdown', releaseFocusBeforeOutsideClick, {capture: true, signal: this.abort.signal});
    document.addEventListener('click', closeFiltersAfterOutsideClick, {capture: true, signal: this.abort.signal});

    document.addEventListener('trekcamp:collection-results-rendered', event => {
      if (this.abort?.signal.aborted || !this.root || !this.root.isConnected) return;
      const sectionId = event.detail?.sectionId;
      // Ignore unrelated sections on the page.
      if (!sectionId || !this.root.querySelector('results-list[section-id="' + CSS.escape(sectionId) + '"]')) return;
      const next = this.filterSignature();
      if (next === this.lastRenderedFilterSignature) return;
      // Only a genuine filter-state change causes Compare items to be pruned.
      // Sorting, pagination and unrelated section refreshes leave them alone.
      if (this.pruneCompareSelections()) {
        this.lastRenderedFilterSignature = next;
      }
    }, options);
    document.addEventListener('theme-drawer:open', this.sync, options);
    document.addEventListener('theme-drawer:close', this.sync, options);
    const filterSignal = this.abort.signal;
    customElements.whenDefined('theme-drawer').then(() => { if (!filterSignal.aborted) this.sync(); });
    this.setMode(false);
    if (this.toggle) this.toggle.hidden = false;
    if (this.root?.querySelector('[data-tc-toolbar-compare]')) {
      this.root.setAttribute('data-tc-toolbar-ready', '');
    }
  }
  disconnectedCallback(){this.root?.removeAttribute('data-tc-toolbar-ready');this.root?.removeAttribute('data-tc-unified-filters');this.root?.removeAttribute('data-tc-compare-mode');this.abort?.abort();this.observer?.disconnect();this.stickySizeObserver?.disconnect();this.root?.style.removeProperty('--tc-compare-sticky-clearance');if(this.stickyBar){this.stickyBar.hidden=true;this.append(this.stickyBar);}this.dialog?.close();delete this.abort;}
}
if(!customElements.get('tc-catalog-tools'))customElements.define('tc-catalog-tools',TrekcampCatalogTools);

/* TREKCAMP: mobile icon-only Sort By, using the existing Shopify native
   facet renderer. Selected option updates preserve all active filters/URLs.
   Clear native selection focus on mobile after any committed change, including
   touch-picker events that do not dispatch pointerdown on the <select>. */
document.addEventListener('change', event => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('select[data-tc-mobile-sort]')) return;
  const root = select.closest('#MainContent[data-template^="collection"]');
  if (!root) return;
  const url = new URL(window.location.href);
  if (select.value) url.searchParams.set('sort_by', select.value);
  else url.searchParams.delete('sort_by');
  const dot = select.closest('.tc-mobile-sort-control')?.querySelector('.tc-sort-active-dot');
  if (dot) dot.hidden = select.value === (select.dataset.defaultSortBy || 'manual');
  url.searchParams.delete('page');
  // After the mobile picker commits a value, immediately release focus;
  // otherwise the browser can leave a persistent orange focus ring.
  if (window.matchMedia('(max-width: 749px)').matches) select.blur();
  if (url.href === window.location.href) return;
  const facets = root.querySelector('#filters-drawer facets-form-component')
    || root.querySelector('facets-form-component');
  if (typeof facets?.updateFiltersByURL === 'function') {
    facets.updateFiltersByURL(url.href);
  } else {
    window.location.assign(url.href);
  }
});
